import { FC, useMemo, useState } from "react";
import { Box, Button, Chip, Divider, IconButton, MenuItem, styled } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays, faChevronDown, faChevronUp, faInputText, faListOl } from "@fortawesome/duotone-light-svg-icons";
import { Checkbox, InputOnlyField, Markdown, Select, Text } from "@ngt/forms";
import ConfigurationFormEditPanel, { ConfigurationBox } from './ConfigurationFormEditPanel';
import { ConfigurationType, IConfigurationValue } from "@/utilities/forms/ConfigurationFormProcessor";
import { IConfigurationFormEditContext } from "@/contexts/forms/ConfigurationFormEditContext";
import { useFormActions } from "@ngt/forms-core";
import { ISourceDataConfigurationFormFieldValue, IFormFieldMetadata, IFormSectionMetadata, IFormVersionMetadata, IFormMetadata, IEventMetadata, IFormFieldValueMetadata, FieldType, SourceDataPriority } from '@/api/dtos';
import ConfigurationFormEditInfo, { ConfigurationDividerBox, ConfigurationFlexBox, ConfigurationTitle, OtherVersionsBox, OtherVersionsItem } from "./ConfigurationFormEditInfo";
import ConfigurationFormEditCopy from "./ConfigurationFormEditCopy";
import ConfigurationFormEditUpdate from "./ConfigurationFormEditUpdate";
import { faExclamationTriangle, faTriangle } from "@fortawesome/pro-solid-svg-icons";

export interface IConfigurationFormEditFieldValueProps {
    context: IConfigurationFormEditContext<IConfigurationValue>
}

interface IOtherVersion {
    type: ConfigurationType;
    eventKey?: string;
    eventMetadata?: IEventMetadata;
    formKey: string;
    formMetadata: IFormMetadata;
    formVersionKey: string;
    formVersionMetadata: IFormVersionMetadata;
    formSectionKey?: string;
    formSectionMetadata?: IFormSectionMetadata;
    formFieldKey: string;
    formFieldMetadata: IFormFieldMetadata;
    formFieldTypeLabel: string;
    formFieldValueKey: string;
    formFieldValueMetadata: IFormFieldValueMetadata;
    formFieldValueConfiguration: ISourceDataConfigurationFormFieldValue;
    configurationPropertyPath: string;
    linked: boolean;
    isGlobal: boolean;
    isTrial: boolean;
}

const ConfigurationFormEditFieldValue: FC<IConfigurationFormEditFieldValueProps> = ({
    context
}) => {
    const {
        configurationPropertyPath
    } = context;

    
    const [openOtherVersions, setOpenOtherVersions] = useState(false);
    
    const actions = useFormActions();

    const allVersions = useMemo(() => {
        if (!context.item.formMetadata?.versions || context.usingTrialConfiguration) {
            return null;
        }

        let currentVersion: IOtherVersion | undefined = undefined;

        const versionKeys = context.item.formMetadata.versions.map(x => x.id);

        const versions: IOtherVersion[] = [];

        const sectionPartialPath = context.item.formSectionKey ? `.sections.${context.item.formSectionKey}` : '';

        let fieldTypeLabel = 'Unknown';

        switch (context.item.formFieldMetadata?.fieldType) {
            case FieldType.Checkbox:
                fieldTypeLabel = 'Checkbox';
                break;
            case FieldType.Ctcae:
                fieldTypeLabel = 'CTCAE';
                break;
            case FieldType.Date:
                fieldTypeLabel = 'Date';
                break;
            case FieldType.File:
                fieldTypeLabel = 'File';
                break;
            case FieldType.MultiSelect:
                fieldTypeLabel = 'MultiSelect';
                break;
            case FieldType.MultilineText:
                fieldTypeLabel = 'MultilineText';
                break;
            case FieldType.Numeric:
                fieldTypeLabel = 'Numeric';
                break;
            case FieldType.Radio:
                fieldTypeLabel = 'Radio';
                break;
            case FieldType.Select:
                fieldTypeLabel = 'Select';
                break;
            case FieldType.Text:
                fieldTypeLabel = 'Text';
                break;
            default:
                fieldTypeLabel = 'Unknown';
                break;
        }

        for (let versionKey of versionKeys) {
            const versionMetadata = context.item.formMetadata?.versions?.find(x => x.id === versionKey);
            const sectionMetadata = versionMetadata?.sections?.find(x => x.id === context.item.formSectionKey);
            const fieldMetadata = versionMetadata?.fields?.find(x => x.id === context.item.formFieldKey);
            const fieldValueMetadata = fieldMetadata?.values?.find(x => x.id === context.item.formFieldValueKey);

            if (fieldMetadata?.fieldType !== context.item.formFieldMetadata?.fieldType) {
                continue;
            }

            if (context.item.formEvents) {
                for (let event of context.item.formEvents[context.item.formKey!]) {
                    const localPropertyPath = `configuration.events.${event.eventKey}.forms.${context.item.formKey!}.versions.${versionKey}${sectionPartialPath}.fields.${context.item.formFieldKey}.values.${context.item.formFieldValueKey}`
                    const localFormVersion = actions.getFieldValue<ISourceDataConfigurationFormFieldValue>(localPropertyPath);

                    if (localFormVersion) {
                        const data = {
                            type: ConfigurationType.Field,
                            eventKey: event.eventKey,
                            eventMetadata: event.eventMetadata,
                            formKey: context.item.formKey!,
                            formMetadata: context.item.formMetadata!,
                            formVersionKey: versionKey!,
                            formVersionMetadata: versionMetadata!,
                            formSectionKey: context.item.formSectionKey,
                            formSectionMetadata: sectionMetadata,
                            formFieldKey: context.item.formFieldKey!,
                            formFieldMetadata: fieldMetadata!,
                            formFieldTypeLabel: fieldTypeLabel,
                            formFieldValueKey: context.item.formFieldValueKey!,
                            formFieldValueMetadata: fieldValueMetadata!,
                            formFieldValueConfiguration: localFormVersion,
                            configurationPropertyPath: localPropertyPath,
                            linked: !!localFormVersion?.linked,
                            isGlobal: false,
                            isTrial: !!context.item.isTrialConfiguration
                        }

                        if (context.configurationPropertyPath !== localPropertyPath) {
                            versions.push(data)
                        }
                        else {
                            currentVersion = data;
                        }
                    }
                }
            }

            const globalPropertyPath = `configuration.forms.${context.item.formKey!}.versions.${versionKey}${sectionPartialPath}.fields.${context.item.formFieldKey}.values.${context.item.formFieldValueKey}`;
            const globalFormVersion = actions.getFieldValue<ISourceDataConfigurationFormFieldValue>(globalPropertyPath);
            
            if (globalFormVersion) {
                const data = {
                    type: ConfigurationType.Field,
                    eventKey: undefined,
                    eventMetadata: undefined,
                    formKey: context.item.formKey!,
                    formMetadata: context.item.formMetadata!,
                    formVersionKey: versionKey!,
                    formVersionMetadata: versionMetadata!,
                    formSectionKey: context.item.formSectionKey,
                    formSectionMetadata: sectionMetadata,
                    formFieldKey: context.item.formFieldKey!,
                    formFieldMetadata: fieldMetadata!,
                    formFieldTypeLabel: fieldTypeLabel,
                    formFieldValueKey: context.item.formFieldValueKey!,
                    formFieldValueMetadata: fieldValueMetadata!,
                    formFieldValueConfiguration: globalFormVersion,
                    configurationPropertyPath: globalPropertyPath,
                    linked: !!globalFormVersion?.linked,
                    isGlobal: !context.item.isTrialConfiguration,
                    isTrial: !!context.item.isTrialConfiguration
                }

                if (context.configurationPropertyPath !== globalPropertyPath) {
                    versions.push(data)
                }
                else {
                    currentVersion = data;
                }
            }

            const trialPropertyPath = `trialConfiguration.forms.${context.item.formKey!}.versions.${versionKey}${sectionPartialPath}.fields.${context.item.formFieldKey}.values.${context.item.formFieldValueKey}`
            const trialFormVersion = actions.getFieldValue<ISourceDataConfigurationFormFieldValue>(trialPropertyPath);
            
            if (trialFormVersion) {
                const data = {
                    type: ConfigurationType.Field,
                    eventKey: undefined,
                    eventMetadata: undefined,
                    formKey: context.item.formKey!,
                    formMetadata: context.item.formMetadata!,
                    formVersionKey: versionKey!,
                    formVersionMetadata: versionMetadata!,
                    formSectionKey: context.item.formSectionKey,
                    formSectionMetadata: sectionMetadata,
                    formFieldKey: context.item.formFieldKey!,
                    formFieldMetadata: fieldMetadata!,
                    formFieldTypeLabel: fieldTypeLabel,
                    formFieldValueKey: context.item.formFieldValueKey!,
                    formFieldValueMetadata: fieldValueMetadata!,
                    formFieldValueConfiguration: trialFormVersion,
                    configurationPropertyPath: trialPropertyPath,
                    linked: !!trialFormVersion?.linked,
                    isGlobal: false,
                    isTrial: true
                }

                if (context.configurationPropertyPath !== trialPropertyPath) {
                    versions.push(data)
                }
                else {
                    currentVersion = data;
                }
            }


        }

        if (versions.length === 0) {
            return null;
        }

        return {
            otherVersions: versions.sort((a, b) => {

                if (a.eventKey !== b.eventKey) {
                    return (a.eventKey ?? 'zzz') < (b.eventKey ?? 'zzz') ? -1 : 1;
                }
                
                if (a.formKey !== b.formKey) {
                    return a.formKey < b.formKey ? -1 : 1;
                }
                
                if (a.formVersionKey !== b.formVersionKey) {
                    return a.formVersionKey < b.formVersionKey ? -1 : 1;
                }
                
                if (a.formSectionKey !== b.formSectionKey) {
                    return (a.formSectionKey ?? 'zzz') < (b.formSectionKey ?? 'zzz') ? -1 : 1;
                }
                
                if (a.formFieldKey !== b.formFieldKey) {
                    return a.formFieldKey < b.formFieldKey ? -1 : 1;
                }
                
                return -1;
            }),
            currentVersion
        };
    }, [context.item.formMetadata?.versions, context.item, context.usingTrialConfiguration, context.usingGlobalConfiguration, actions])

    const otherVersionContent = useMemo(() => {

        if (!allVersions || !allVersions.otherVersions) {
            return null;
        }

        return (
            <OtherVersionsBox>
                {
                    allVersions.otherVersions.map((x, i, a) => {            
                        return (
                            <OtherVersionsItem key={x.configurationPropertyPath}>
                                <ConfigurationFormEditInfo 
                                    {...x}
                                    dividers
                                />
                                <ConfigurationDividerBox>
                                    <Divider />
                                </ConfigurationDividerBox>
                                <ConfigurationFlexBox sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    {
                                        !!allVersions.currentVersion && (
                                            <>
                                                <ConfigurationFormEditCopy 
                                                    target={allVersions.currentVersion}
                                                    source={x}
                                                />
                                                <ConfigurationFormEditUpdate 
                                                    target={x}
                                                    source={allVersions.currentVersion}
                                                />
                                            </>
                                        )
                                    }
                                </ConfigurationFlexBox>
                            </OtherVersionsItem>
                        )
                    })
                }
            </OtherVersionsBox>
        )
    }, [allVersions, context.item]);
    
    return (
        <ConfigurationFormEditPanel 
            context={context} 
            type="Field Value"
            typeIcon={faListOl}
        >
            {
                allVersions?.otherVersions && (
                    <>
                        <ConfigurationTitle 
                            variant="h2" 
                            {...{component: Button} as any} 
                            color='inherit' 
                            sx={{ 
                                display: 'flex', 
                                padding: 0,
                                textTransform: 'none'
                            }}
                            onClick={() => setOpenOtherVersions(c => !c)}
                        >
                            Other Versions
                            <Chip 
                                label={allVersions.otherVersions.length}
                                color="primary"
                                sx={{
                                    ml: 'auto'
                                }}
                                size="small"
                            />
                            <IconButton component="div">
                                <FontAwesomeIcon icon={openOtherVersions ? faChevronUp : faChevronDown} />
                            </IconButton>
                        </ConfigurationTitle>
                        {
                            openOtherVersions && (
                                otherVersionContent
                            )
                        }
                    </>
                )
            }
            <ConfigurationTitle variant="h2">
                Options
            </ConfigurationTitle>
            <ConfigurationBox>
                <InputOnlyField 
                    label="Override Name"
                    propertyPath={`${configurationPropertyPath}.name`}
                    component={Text}
                    margin="normal"
                />
                <InputOnlyField 
                    label="Priority"
                    propertyPath={`${configurationPropertyPath}.priority`}
                    component={Select}
                    FormControlProps={{
                        margin: 'normal'
                    }}
                >
                    <MenuItem
                        value={SourceDataPriority.Low}
                    >
                        <Box sx={{display: 'flex', width: 'auto', flex: '1 1 auto'}}>
                            Low 
                            <Box sx={{ml: 'auto', color: 'success.main', transform: 'rotate(180deg)'}}>
                                <FontAwesomeIcon icon={faTriangle} />
                            </Box>
                        </Box>
                    </MenuItem>
                    <MenuItem
                        value={SourceDataPriority.Normal}
                    >
                        Normal
                    </MenuItem>
                    <MenuItem
                        value={SourceDataPriority.High}
                    >
                        <Box sx={{display: 'flex', width: 'auto', flex: '1 1 auto'}}>
                            High
                            <Box sx={{ml: 'auto', color: 'warning.main'}}>
                                <FontAwesomeIcon icon={faTriangle} />
                            </Box>
                        </Box>
                    </MenuItem>
                    <MenuItem
                        value={SourceDataPriority.Critical}
                    >
                        <Box sx={{display: 'flex', width: 'auto', flex: '1 1 auto'}}>
                            Critical
                            <Box sx={{ml: 'auto', color: 'error.main'}}>
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                            </Box>
                        </Box>
                    </MenuItem>
                </InputOnlyField>
            </ConfigurationBox>

            <ConfigurationTitle variant="h4">
                Alerts
            </ConfigurationTitle>
            <ConfigurationBox>
                <InputOnlyField 
                    label="Information"
                    propertyPath={`${configurationPropertyPath}.alertInformation`}
                    component={Markdown}
                    margin="normal"
                    //color="info"
                />
                <InputOnlyField 
                    label="Warning"
                    propertyPath={`${configurationPropertyPath}.alertWarning`}
                    component={Markdown}
                    margin="normal"
                    //color="warning"
                />
                <InputOnlyField 
                    label="Error"
                    propertyPath={`${configurationPropertyPath}.alertError`}
                    component={Markdown}
                    margin="normal"
                    //color="error"
                />
                <InputOnlyField 
                    label="Success"
                    propertyPath={`${configurationPropertyPath}.alertSuccess`}
                    component={Markdown}
                    margin="normal"
                    //color="success"
                />
            </ConfigurationBox>
        </ConfigurationFormEditPanel>
    )
}

export default ConfigurationFormEditFieldValue;